import { A } from "@solidjs/router";
import { Accessor, Match, Show, Switch, createSignal } from "solid-js";
import {
  instoreTagIcon,
  noResultsFound,
  onlineTagIcon,
  placeholderCard,
  subscriptionTagIcon,
} from "~/assets/assets";
import { HubbleEvent, webEventManager } from "~/data/events";
import { Product, VoucherProductStatus } from "~/server/types/search";
import HubbleImage from "~/widgets/hubble_image";

export function SearchResults(props: {
  result: Product[];
  noResultsFound: boolean;
  isSearchActive: Accessor<boolean>;
  onBrandClick: () => void;
  isFetchingResults: Accessor<boolean>;
  isProcurment?: boolean;
  openDenominationModal?: (product: Product) => void;
}) {
  return (
    <Switch>
      <Match when={props.result.length > 0 && props.isSearchActive()}>
        <div class="mt-1 flex-col ">
          {props.result.map((product) => (
            <div
              class={`${props.isFetchingResults() ? "opacity-40" : ""} transition-opacity`}
            >
              <Switch>
                <Match when={props.isProcurment}>
                  <div
                    onClick={() => {
                      if (
                        product.voucherProduct.status == "ACTIVE" &&
                        props.openDenominationModal
                      ) {
                        props.openDenominationModal!(product);
                      }
                    }}
                  >
                    <SearchResultCard
                      imageUrl={product.voucherProduct.logoUrl}
                      name={product.voucherProduct.title}
                      brandKey={product.brandKey}
                      onBrandClick={props.onBrandClick}
                      discountPercentage={
                        product.voucherProduct.discountPercentage
                      }
                      status={product.voucherProduct.status}
                      isProcurment={props.isProcurment}
                      iconClass="h-10 w-10"
                      titleClass="text-mediumBold text-basePrimaryDark"
                      subtitleClass="text-f12"
                      tag={
                        product.tags.includes("OFFLINE")
                          ? {
                              icon: instoreTagIcon,
                              text: "In-store",
                            }
                          : product.tags.includes("SUBSCRIPTION")
                            ? {
                                icon: subscriptionTagIcon,
                                text: "Subscription",
                              }
                            : product.tags.includes("ONLINE")
                              ? {
                                  icon: onlineTagIcon,
                                  text: "Online",
                                }
                              : undefined
                      }
                    />
                  </div>
                </Match>
                <Match when={true}>
                  <A
                    href={
                      product.voucherProduct.status == "ACTIVE"
                        ? `/buy-gift-card/${product.brandKey}`
                        : ""
                    }
                  >
                    <SearchResultCard
                      imageUrl={product.voucherProduct.logoUrl}
                      name={product.voucherProduct.title}
                      brandKey={product.brandKey}
                      onBrandClick={props.onBrandClick}
                      discountPercentage={
                        product.voucherProduct.discountPercentage
                      }
                      status={product.voucherProduct.status}
                      isProcurment={false}
                      iconClass="h-12 w-12"
                      titleClass="text-bold text-basePrimaryDark"
                      subtitleClass="text-bold text-successDark"
                      tag={
                        product.tags.includes("OFFLINE")
                          ? {
                              icon: instoreTagIcon,
                              text: "In-store",
                            }
                          : product.tags.includes("SUBSCRIPTION")
                            ? {
                                icon: subscriptionTagIcon,
                                text: "Subscription",
                              }
                            : product.tags.includes("ONLINE")
                              ? {
                                  icon: onlineTagIcon,
                                  text: "Online",
                                }
                              : undefined
                      }
                    />
                  </A>
                </Match>
              </Switch>
            </div>
          ))}
        </div>
      </Match>
      <Match when={props.noResultsFound && props.isSearchActive()}>
        <div class="mt-1">
          <div class="my-3 flex h-full w-full flex-col items-center justify-center">
            <HubbleImage
              src={noResultsFound}
              class="h-[80px] w-[80px]"
              alt="no results found"
            />

            <div class="mt-2  max-w-[250px] text-center text-[15px] text-normal text-textNormal">
              Please try another keyword for brand or category
            </div>
          </div>
        </div>
      </Match>
    </Switch>
  );
}

function SearchResultCard(props: {
  imageUrl: string;
  name: string;
  brandKey: string;
  onBrandClick: () => void;
  discountPercentage: number;
  status: VoucherProductStatus;
  isProcurment?: boolean;
  titleClass: string;
  subtitleClass: string;
  iconClass: string;
  tag?: {
    icon: string;
    text: string;
  };
}) {
  const [showIcon, setShowIcon] = createSignal(false);
  return (
    <div
      onMouseEnter={() => setShowIcon(true)}
      onMouseLeave={() => setShowIcon(false)}
      class={`group mx-3  flex cursor-pointer flex-row items-center border-b border-baseTertiaryMedium py-2 darkTheme:border-[#313538] ${props.isProcurment ? "gap-2" : "gap-3"}`}
      onClick={() => {
        props.onBrandClick();
        webEventManager.sendEvent(HubbleEvent.CLICK_SEARCH_RESULT, {
          brandKey: props.brandKey,
          brandName: props.name,
        });
      }}
    >
      <HubbleImage
        src={props.imageUrl}
        class={`${props.iconClass} rounded-full ${props.status == "INACTIVE" ? "opacity-50" : ""}`}
        alt={`${props.name} logo`}
        errorImage={placeholderCard}
      />
      <div class="flex-1 flex-col ">
        <div
          class={`${props.titleClass} ${props.status == "INACTIVE" ? "opacity-50" : ""}`}
        >
          {props.name}
        </div>
        <div class="flex flex-row items-center gap-2">
          <Show when={props.discountPercentage > 0}>
            <div class={props.subtitleClass}>
              {props.discountPercentage}% off
            </div>
          </Show>
          <Show when={props.tag}>
            <div class="flex flex-row items-center gap-1">
              <div class="mr-1 h-1 w-1 rounded-full bg-baseTertiaryMedium" />
              <img
                src={props.tag!.icon}
                alt={props.tag!.text}
                class={`${props.isProcurment ? "h-3 w-3" : "h-4 w-4"}`}
              />
              <div
                class={`${props.isProcurment ? "text-f12" : "text-normal"} text-textNormal`}
              >
                {props.tag!.text}
              </div>
            </div>
          </Show>
        </div>
        <Show when={props.status == "INACTIVE"}>
          <div class="text-f12Bold text-errorDark">Temporarily unavailable</div>
        </Show>
      </div>
      {showIcon() && (
        <div class="pointer-events-none inset-y-0 right-0 flex items-center ">
          <i
            class={
              "ph ph-arrow-right text-basePrimaryDark darkTheme:text-textNormal "
            }
          ></i>
        </div>
      )}
    </div>
  );
}
